import {
  countryDisplayName,
  enabledDestinations,
  SHIPPING_ADDRESS_FIELD_MAX,
  SHIPPING_ADDRESS_FIELDS,
  type ShippingAddress,
  shippingAddressFieldIssue,
  shippingAddressSchema,
  US_STATE_OPTIONS,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { FactGroup } from "./Facts";

type Draft = Record<keyof ShippingAddress, string>;

function toDraft(address: ShippingAddress | null): Draft {
  const draft = {} as Draft;
  for (const field of SHIPPING_ADDRESS_FIELDS) draft[field.key] = address?.[field.key] ?? "";
  return draft;
}

/** The draft without its blank fields, so an untouched optional field is omitted, not stored as "". */
function withoutBlanks(draft: Draft): Partial<Draft> {
  const out: Partial<Draft> = {};
  for (const field of SHIPPING_ADDRESS_FIELDS) {
    const value = draft[field.key].trim();
    if (value) out[field.key] = value;
  }
  return out;
}

const WIDE_FIELDS: ReadonlySet<keyof ShippingAddress> = new Set(["addressLine1", "addressLine2"]);

const ISSUE_MESSAGES = {
  invalid_region: "Pick the state or territory.",
  postal_code_mismatch: "The postal code does not fit the country.",
} as const;

const US_STATE_LABELS = Object.fromEntries(US_STATE_OPTIONS.map((s) => [s.code, s.name]));

/**
 * The end user's shipping address as staff record it (boxed parity: the Account Manager's
 * Update Address form). The country is one of the environment's enabled shipping destinations,
 * the same list the shopper's own form offers, and a US address picks a state. An order copies
 * its own address at creation; this one is a reference. Editors get an Add/Edit dialog in the
 * heading; viewers read.
 */
export function ShippingAddressSection({
  endUserId,
  address,
}: {
  endUserId: string;
  address: ShippingAddress | null;
}) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const storefront = useStorefrontConfigQuery().query.data;
  const destinations = storefront
    ? enabledDestinations(
        storefront.config.shippingDestinations ?? storefront.shipping.platformDefault,
      )
    : [];
  const countryLabels = Object.fromEntries(
    destinations.map((d) => [d.countryCode, countryDisplayName(d.countryCode)]),
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => toDraft(address));
  const save = useMutation(
    trpc.users.setShippingAddress.mutationOptions({
      onSuccess: (result) => {
        toast.success(result.shippingAddress ? "Address saved." : "Address cleared.");
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
      },
    }),
  );
  const parsed = shippingAddressSchema.safeParse(withoutBlanks(draft));
  const issue = parsed.success ? shippingAddressFieldIssue(parsed.data) : null;
  const canSave = parsed.success && issue === null && destinations.length > 0;
  const isUs = draft.country === "US";

  return (
    <FactGroup
      title="Shipping address"
      action={
        can("editor") && (
          <Dialog
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (next) setDraft(toDraft(address));
            }}
          >
            <DialogTrigger render={<Button type="button" variant="ghost" size="sm" />}>
              {address ? "Edit" : "Add"}
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Shipping address</DialogTitle>
                <DialogDescription>
                  Every field except the second line and the phone number is required; the country
                  is one this environment ships to.
                </DialogDescription>
              </DialogHeader>
              <form
                className="grid gap-3 sm:grid-cols-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (parsed.success && canSave) {
                    save.mutate({ endUserId, shippingAddress: parsed.data });
                  }
                }}
              >
                {SHIPPING_ADDRESS_FIELDS.map((field) => (
                  <div
                    key={field.key}
                    className={`grid gap-2 ${WIDE_FIELDS.has(field.key) ? "sm:col-span-2" : ""}`}
                  >
                    <Label htmlFor={`address-${field.key}`}>{field.label}</Label>
                    {field.key === "country" ? (
                      <Select
                        value={draft.country || null}
                        items={countryLabels}
                        onValueChange={(value) =>
                          // A new country empties the region: a state code means nothing elsewhere.
                          setDraft({ ...draft, country: value ?? "", region: "" })
                        }
                      >
                        <SelectTrigger id="address-country" className="w-full">
                          <SelectValue placeholder="Choose a country" />
                        </SelectTrigger>
                        <SelectContent>
                          {destinations.map((d) => (
                            <SelectItem key={d.countryCode} value={d.countryCode}>
                              {countryLabels[d.countryCode]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.key === "region" && isUs ? (
                      <Select
                        value={draft.region || null}
                        items={US_STATE_LABELS}
                        onValueChange={(value) => setDraft({ ...draft, region: value ?? "" })}
                      >
                        <SelectTrigger id="address-region" className="w-full">
                          <SelectValue placeholder="Choose a state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATE_OPTIONS.map((s) => (
                            <SelectItem key={s.code} value={s.code}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`address-${field.key}`}
                        value={draft[field.key]}
                        maxLength={SHIPPING_ADDRESS_FIELD_MAX}
                        required={field.required}
                        autoComplete="off"
                        onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                      />
                    )}
                  </div>
                ))}
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  {destinations.length === 0
                    ? "Shipping is not configured for this environment (the tenant's Shipping page)."
                    : issue
                      ? ISSUE_MESSAGES[issue]
                      : null}
                </p>
                <DialogFooter className="sm:col-span-2">
                  {address && (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={save.isPending}
                      onClick={() => save.mutate({ endUserId, shippingAddress: null })}
                    >
                      Clear address
                    </Button>
                  )}
                  <DialogClose render={<Button type="button" variant="outline" />}>
                    Cancel
                  </DialogClose>
                  <Button type="submit" disabled={save.isPending || !canSave}>
                    {save.isPending ? "Saving…" : "Save address"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )
      }
    >
      {address ? (
        <address className="grid gap-0.5 text-sm not-italic">
          <span>
            {address.firstName} {address.lastName}
          </span>
          <span>{address.addressLine1}</span>
          {address.addressLine2 && <span>{address.addressLine2}</span>}
          <span>
            {address.locality}, {address.region} {address.postalCode}
          </span>
          <span>{countryDisplayName(address.country)}</span>
          {address.phoneNumber && (
            <span className="text-muted-foreground">{address.phoneNumber}</span>
          )}
        </address>
      ) : (
        <p className="text-sm text-muted-foreground">None on file.</p>
      )}
    </FactGroup>
  );
}
